/**
 * AllStarTeamsSection - Component for managing All-Star Game teams and rosters
 */
import { Plus, X, Users, Wand2 } from "lucide-react";
import { extractDominantColor } from "../../../utils/colorExtractor";

export const AllStarTeamsSection = ({ formData, setFormData }) => {
  const updateTeam1 = (updates) => {
    setFormData({
      ...formData,
      all_star_team_1: { ...(formData.all_star_team_1 || {}), ...updates }
    });
  };

  const updateTeam2 = (updates) => {
    setFormData({
      ...formData,
      all_star_team_2: { ...(formData.all_star_team_2 || {}), ...updates }
    });
  };

  const addPlayerToTeam = (teamNum) => {
    const teamKey = `all_star_team_${teamNum}`;
    const roster = formData[teamKey]?.roster || [];
    setFormData({
      ...formData,
      [teamKey]: {
        ...formData[teamKey],
        roster: [...roster, { name: '', position: '', school: '' }]
      }
    });
  };

  const updatePlayerInTeam = (teamNum, idx, field, value) => {
    const teamKey = `all_star_team_${teamNum}`;
    const roster = [...(formData[teamKey]?.roster || [])];
    roster[idx] = { ...roster[idx], [field]: value };
    setFormData({
      ...formData,
      [teamKey]: { ...formData[teamKey], roster }
    });
  };

  const removePlayerFromTeam = (teamNum, idx) => {
    const teamKey = `all_star_team_${teamNum}`;
    const roster = (formData[teamKey]?.roster || []).filter((_, i) => i !== idx);
    setFormData({
      ...formData,
      [teamKey]: { ...formData[teamKey], roster }
    });
  };

  const extractColorFromLogo = async (teamNum, logoUrl) => {
    if (!logoUrl) return;
    try {
      const color = await extractDominantColor(logoUrl);
      if (color) {
        const teamKey = `all_star_team_${teamNum}`;
        setFormData({
          ...formData,
          [teamKey]: { ...formData[teamKey], color }
        });
      }
    } catch (e) {
      console.error('Error extracting color:', e);
    }
  };

  const renderTeamSection = (teamNum, title, defaultColor) => {
    const teamKey = `all_star_team_${teamNum}`;
    const team = formData[teamKey] || {};
    const updateTeam = teamNum === 1 ? updateTeam1 : updateTeam2;

    return (
      <div className="p-4 bg-zinc-800/50 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-secondary text-xs font-bold uppercase text-zinc-300">{title}</h5>
          {team.color && (
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: team.color }} />
          )}
        </div>
        <div className="space-y-3">
          {/* Team Name */}
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Team Name *</label>
            <input 
              type="text"
              value={team.name || ''}
              onChange={(e) => updateTeam({ name: e.target.value })}
              placeholder={`e.g., ${teamNum === 1 ? 'East' : 'West'} All-Stars`}
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
          
          {/* Color and Logo */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Team Color</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={team.color || defaultColor}
                  onChange={(e) => updateTeam({ color: e.target.value })}
                  className="w-10 h-8 bg-transparent border-0 cursor-pointer"
                />
                <input 
                  type="text" 
                  value={team.color || defaultColor}
                  onChange={(e) => updateTeam({ color: e.target.value })}
                  className="flex-1 bg-zinc-900 border border-white/10 text-white px-2 py-1 text-xs font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Logo URL</label>
              <input 
                type="text"
                value={team.logo_url || ''}
                onChange={(e) => updateTeam({ logo_url: e.target.value })}
                placeholder="https://..."
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
              />
              {/* Logo Preview with Extract Button */}
              {team.logo_url && (
                <div className="mt-2 p-2 bg-zinc-900/50 border border-white/5 rounded flex items-center gap-2">
                  <img 
                    src={team.logo_url} 
                    alt={`${title} logo`}
                    className="w-10 h-10 object-contain bg-white/5 p-1 rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => extractColorFromLogo(teamNum, team.logo_url)}
                    className="flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-bold rounded"
                  >
                    <Wand2 className="w-2.5 h-2.5" /> Extract
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Roster Section */}
          {!formData.roster_tbd && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] text-zinc-500 uppercase">
                  Roster ({(team.roster || []).length} players)
                </label>
                <button
                  type="button"
                  onClick={() => addPlayerToTeam(teamNum)}
                  className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Player
                </button>
              </div>
              
              {(team.roster || []).length === 0 ? (
                <p className="text-[10px] text-zinc-600 italic">No players added yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(team.roster || []).map((player, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-zinc-900/50 p-2">
                      <input
                        type="text"
                        value={player.name}
                        onChange={(e) => updatePlayerInTeam(teamNum, idx, 'name', e.target.value)}
                        placeholder="Name"
                        className="flex-1 bg-zinc-800 border border-white/10 text-white px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        value={player.position}
                        onChange={(e) => updatePlayerInTeam(teamNum, idx, 'position', e.target.value)}
                        placeholder="Pos"
                        className="w-12 bg-zinc-800 border border-white/10 text-white px-2 py-1 text-xs text-center"
                      />
                      <input
                        type="text"
                        value={player.school}
                        onChange={(e) => updatePlayerInTeam(teamNum, idx, 'school', e.target.value)}
                        placeholder="School"
                        className="w-24 bg-zinc-800 border border-white/10 text-white px-2 py-1 text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => removePlayerFromTeam(teamNum, idx)}
                        className="text-zinc-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="border border-purple-500/30 bg-purple-500/5 p-6">
      <h4 className="font-secondary text-sm font-bold uppercase text-purple-400 mb-4 flex items-center gap-2">
        <Users className="w-4 h-4" /> All-Star Teams
      </h4>
      
      {/* Roster TBD Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <input 
          type="checkbox" 
          id="roster_tbd"
          checked={formData.roster_tbd}
          onChange={(e) => setFormData({ ...formData, roster_tbd: e.target.checked })}
          className="w-4 h-4"
        />
        <label htmlFor="roster_tbd" className="text-sm text-zinc-400">
          Rosters TBD (to be announced)
        </label>
      </div>

      {/* Two Team Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderTeamSection(1, "Team 1", "#003087")}
        {renderTeamSection(2, "Team 2", "#C8102E")}
      </div>
      
      {/* TBD Notice */}
      {formData.roster_tbd && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
          <strong>Rosters TBD:</strong> Player rosters will be displayed as "To Be Announced" on the public event page.
        </div>
      )}
    </div>
  );
};

export default AllStarTeamsSection;
